import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * Supports lazy loading via IntersectionObserver
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // Check if lazy loading is enabled (default to true for performance)
    const lazyLoad = this.getAttribute('data-lazy-load') !== 'false';

    if (lazyLoad) {
      this.setupLazyLoading();
    } else {
      this.loadVideo();
    }
  }

  /**
   * Set up IntersectionObserver for lazy loading
   */
  setupLazyLoading() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadVideo();
            observer.unobserve(this);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
      }
    );

    observer.observe(this);
  }

  /**
   * Load and play the video
   */
  loadVideo() {
    const { videoSources, videoElement } = this.refs;

    // Set video sources
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Load and play the video
    videoElement.load();
    
    // Attempt to play (will auto-play if muted)
    const playPromise = videoElement.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Auto-play was prevented, user interaction required
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Pause video when element is removed
    if (this.refs.videoElement) {
      this.refs.videoElement.pause();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
